import {Box, useMediaQuery, useTheme} from '@mui/material';
import {ReactNode, useMemo} from 'react';
import {MatrixEvent} from 'matrix-js-sdk';
import {formatTime} from '@/components/matrix-chat/utils/helper-functions';
import {EventPosition} from '@/types/matrix';
import {useMatrixContext} from '@/contexts/matrix/client';
import SenderName from '@/components/matrix-chat/message/text-message/user-message/sender-name';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import Linkify from 'linkify-react';
import IconPanel from './icon-panel';
import {useMatrixRoomContext} from '@/contexts/matrix/room';

const borderRadius = {
  single: {
    borderTopRightRadius: '16px',
    borderBottomRightRadius: '16px',
    borderTopLeftRadius: '16px',
    borderBottomLeftRadius: '16px'
  },
  first: {
    borderTopRightRadius: '18px',
    borderBottomRightRadius: '8px',
    borderTopLeftRadius: '18px',
    borderBottomLeftRadius: '4px'
  },
  middle: {
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
    borderTopLeftRadius: '4px',
    borderBottomLeftRadius: '4px'
  },
  last: {
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '18px',
    borderTopLeftRadius: '4px',
    borderBottomLeftRadius: '18px'
  }
};

const borderRadiusOwn = {
  single: {
    borderTopRightRadius: '16px',
    borderBottomRightRadius: '16px',
    borderTopLeftRadius: '16px',
    borderBottomLeftRadius: '16px'
  },
  first: {
    borderTopRightRadius: '18px',
    borderBottomRightRadius: '4px',
    borderTopLeftRadius: '18px',
    borderBottomLeftRadius: '8px'
  },
  middle: {
    borderTopRightRadius: '4px',
    borderBottomRightRadius: '4px',
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px'
  },
  last: {
    borderTopRightRadius: '4px',
    borderBottomRightRadius: '18px',
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '18px'
  }
};

interface ContentDisplayBoxProps {
  eventPosition: EventPosition;
  event: MatrixEvent;
  children?: ReactNode;
  messageText?: string;
  onDownloadIconPanel?: () => void;
  onDeleteIconPanel?: () => void;
  isTextMessage?: boolean;
}

const ContentDisplayBox: React.FC<ContentDisplayBoxProps> = ({
  eventPosition,
  event,
  children,
  messageText,
  onDownloadIconPanel,
  onDeleteIconPanel
}) => {
  const {matrixUserId} = useMatrixContext();

  const {currentHoveredEvent} = useMatrixRoomContext();

  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const effectiveMatrixEvent = event.getEffectiveEvent();

  const isOwnMessage = effectiveMatrixEvent.sender === matrixUserId;

  const formattedTime = useMemo(() => {
    const timestamp = new Date(
      effectiveMatrixEvent.origin_server_ts
    ).toISOString();
    return formatTime(timestamp);
  }, [effectiveMatrixEvent]);

  const isDeletedMessage = useMemo(() => {
    const isDeleted =
      effectiveMatrixEvent.unsigned &&
      effectiveMatrixEvent.unsigned.redacted_because?.content.reason ===
        'User deleted message';
    return isDeleted;
  }, [effectiveMatrixEvent]);

  const canDeleteMessage = isOwnMessage && !isDeletedMessage;

  const showName = !!(
    (eventPosition === 'first' || eventPosition === 'single') &&
    !isOwnMessage
  );

  const showTime = !!(eventPosition === 'last' || eventPosition === 'single');

  const timePartMargin = isSmallScreen ? {marginRight: 7} : {marginRight: 9};

  const timePartFontStyle = isDeletedMessage ? {fontStyle: 'italic'} : {};

  const timeColor = isOwnMessage
    ? theme.palette.customColors?.messageText
    : theme.palette.customColors?.messageTextOwn;

  const backgroundColor = isOwnMessage
    ? theme.palette.customColors?.messageBackgroundOwn || '#000000'
    : theme.palette.customColors?.messageBackground || '#000000';

  const boxProperties = {
    backgroundColor: backgroundColor,
    marginRight: isOwnMessage ? 2 : 0,
    marginLeft: isOwnMessage ? 0 : 2,
    position: 'relative',
    ...(isOwnMessage
      ? borderRadiusOwn[eventPosition]
      : borderRadius[eventPosition]),
    overflow: 'hidden',
    maxWidth: {xs: '150px', md: '300px'}
  };

  const messagePartSxProps = {
    marginLeft: 1,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    color: isOwnMessage
      ? theme.palette.customColors?.messageText || '#000000'
      : theme.palette.customColors?.messageTextOwn || '#000000',
    flexBasis: 'auto'
  };

  const senderNamePartMargin = isSmallScreen
    ? {marginLeft: 1, marginTop: 0.5}
    : {marginLeft: 1, marginTop: 1};

  return (
    <Box sx={{position: 'relative'}}>
      {currentHoveredEvent === effectiveMatrixEvent.event_id && (
        <IconPanel
          onDelete={canDeleteMessage ? onDeleteIconPanel : undefined}
          onDownload={onDownloadIconPanel}
          isOwnMessage={isOwnMessage}
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            right: isOwnMessage ? 'auto' : '-40px',
            left: isOwnMessage ? '-80px' : 'auto'
          }}
        />
      )}
      <Box
        sx={{
          ...boxProperties
        }}
      >
        {showName && (
          <SenderName
            showUserProfileByClick={true}
            senderId={effectiveMatrixEvent.sender}
            additionalMarginBottom={1}
            sx={senderNamePartMargin}
          />
        )}

        {children}
        <div
          style={{
            display: 'flex',
            flexDirection: messageText ? 'row' : 'column',
            justifyContent: 'space-between',
            marginBottom: 3,
            marginTop: 2
          }}
        >
          {messageText && (
            <ResponsiveTypography
              sx={messagePartSxProps}
              smallAllSizes={isDeletedMessage ? true : false}
            >
              <Linkify
                options={{
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  attributes: {
                    style: {
                      color: theme.palette.customColors?.urlLink
                    }
                  }
                }}
              >
                {messageText}
              </Linkify>
            </ResponsiveTypography>
          )}
          {showTime && (
            <div
              style={{
                textAlign: 'right',
                alignSelf: 'flex-end',
                ...timePartMargin
              }}
            >
              <ResponsiveTypography
                smallAllSizes={true}
                sx={{
                  fontWeight: 'bold',
                  color: timeColor,
                  ...timePartFontStyle
                }}
              >
                {formattedTime}
              </ResponsiveTypography>
            </div>
          )}
        </div>
      </Box>
    </Box>
  );
};

export default ContentDisplayBox;
